import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Stethoscope, Trash2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { cacheApi } from '@/api/cache';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import {
  CACHE_TTL_MAX_SECONDS,
  CACHE_TTL_MIN_SECONDS,
  type CacheConfigPatch,
  type CacheConfigView,
  type CacheEndpointConfig,
} from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export function CacheTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const query = useQuery({
    queryKey: ['org', 'cache', currentBotId],
    queryFn: () => cacheApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['org', 'cache', currentBotId] });
    void queryClient.invalidateQueries({ queryKey: ['bot', 'system', currentBotId] });
  };

  const updateMutation = useMutation({
    mutationFn: (patch: CacheConfigPatch) => cacheApi.update(currentBotId!, patch),
    onSuccess: () => {
      toast.success(t('cache.savedToast', { defaultValue: 'Cache configuration saved' }));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const purgeMutation = useMutation({
    mutationFn: () => cacheApi.purge(currentBotId!),
    onSuccess: () => {
      toast.success(t('cache.purgedToast', { defaultValue: 'Cache emptied' }));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          {t('cache.selectBot', { defaultValue: 'Select a bot to configure its response cache.' })}
        </CardContent>
      </Card>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          {t('common.loading')}
        </CardContent>
      </Card>
    );
  }

  if (query.error || !query.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">{unwrapError(query.error)}</CardContent>
      </Card>
    );
  }

  const config = query.data;
  const pending = updateMutation.isPending;

  return (
    <div className="space-y-6">
      <ActivationCard
        config={config}
        canManage={canManage}
        onToggle={(enabled) => updateMutation.mutate({ enabled })}
        pending={pending}
      />
      <EndpointCard
        intent="diagnose"
        icon={Stethoscope}
        config={config.diagnose}
        masterEnabled={config.enabled}
        canManage={canManage}
        pending={pending}
        onPatch={(patch) => updateMutation.mutate({ diagnose: patch })}
      />
      <EndpointCard
        intent="support"
        icon={LifeBuoy}
        config={config.support}
        masterEnabled={config.enabled}
        canManage={canManage}
        pending={pending}
        onPatch={(patch) => updateMutation.mutate({ support: patch })}
      />
      <MaintenanceCard
        config={config}
        canManage={canManage}
        pending={purgeMutation.isPending}
        onPurge={() => purgeMutation.mutate()}
      />
    </div>
  );
}

function ActivationCard({
  config,
  canManage,
  onToggle,
  pending,
}: {
  config: CacheConfigView;
  canManage: boolean;
  onToggle: (enabled: boolean) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {t('cache.activation.title', { defaultValue: 'Activation' })}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('cache.activation.subtitle', {
              defaultValue:
                'Reuse a previous answer instead of calling the AI service again when the exact same request comes in. Only resolved answers are cached.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.enabled ? 'success' : 'outline'} className="text-[10px]">
            {config.enabled
              ? t('cache.activation.on', { defaultValue: 'Enabled' })
              : t('cache.activation.off', { defaultValue: 'Disabled' })}
          </Badge>
          <Switch checked={config.enabled} disabled={!canManage || pending} onCheckedChange={onToggle} />
        </div>
      </CardHeader>
    </Card>
  );
}

function EndpointCard({
  intent,
  icon: Icon,
  config,
  masterEnabled,
  canManage,
  pending,
  onPatch,
}: {
  intent: 'diagnose' | 'support';
  icon: typeof Stethoscope;
  config: CacheEndpointConfig;
  masterEnabled: boolean;
  canManage: boolean;
  pending: boolean;
  onPatch: (patch: Partial<CacheEndpointConfig>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [ttl, setTtl] = React.useState(String(config.ttlSeconds));

  React.useEffect(() => {
    setTtl(String(config.ttlSeconds));
  }, [config.ttlSeconds]);

  const parsed = Number(ttl);
  const valid =
    Number.isInteger(parsed) && parsed >= CACHE_TTL_MIN_SECONDS && parsed <= CACHE_TTL_MAX_SECONDS;
  const dirty = ttl !== String(config.ttlSeconds);
  const inactive = !masterEnabled || !config.enabled;
  const inputId = `cache-ttl-${intent}`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <div>
            <CardTitle className="text-base">
              {t(`cache.${intent}.title`, {
                defaultValue: intent === 'diagnose' ? 'POST /diagnose' : 'POST /support',
              })}
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(`cache.${intent}.subtitle`, {
                defaultValue:
                  intent === 'diagnose'
                    ? 'Incident diagnostics on a shipment, order or tracking number.'
                    : 'Free-form customer support requests.',
              })}
            </p>
          </div>
        </div>
        <Switch
          checked={config.enabled}
          disabled={!canManage || pending || !masterEnabled}
          onCheckedChange={(enabled) => onPatch({ enabled })}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor={inputId}>
            {t('cache.ttl.label', { defaultValue: 'Validity (seconds)' })}
          </Label>
          <Input
            id={inputId}
            type="number"
            min={CACHE_TTL_MIN_SECONDS}
            max={CACHE_TTL_MAX_SECONDS}
            value={ttl}
            disabled={!canManage || pending || inactive}
            onChange={(e) => setTtl(e.target.value)}
            className="mt-1 max-w-[12rem]"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {valid
              ? t('cache.ttl.hint', {
                  defaultValue: '{{seconds}} s ≈ {{human}}. Between {{min}} s and {{max}} s.',
                  seconds: parsed,
                  human: humanizeSeconds(parsed, t),
                  min: CACHE_TTL_MIN_SECONDS,
                  max: CACHE_TTL_MAX_SECONDS,
                })
              : t('cache.ttl.invalid', {
                  defaultValue: 'Enter a whole number between {{min}} and {{max}} seconds.',
                  min: CACHE_TTL_MIN_SECONDS,
                  max: CACHE_TTL_MAX_SECONDS,
                })}
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => setTtl(String(config.ttlSeconds))}
            disabled={!dirty || pending}
          >
            {t('common.reset', { defaultValue: 'Reset' })}
          </Button>
          <Button
            onClick={() => onPatch({ ttlSeconds: parsed })}
            disabled={!canManage || !dirty || !valid || pending}
            className="gap-1.5"
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MaintenanceCard({
  config,
  canManage,
  pending,
  onPurge,
}: {
  config: CacheConfigView;
  canManage: boolean;
  pending: boolean;
  onPurge: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('cache.maintenance.title', { defaultValue: 'Maintenance' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('cache.maintenance.subtitle', {
            defaultValue:
              'Empty the cache so the next request runs the agent again. Useful after changing an integration or a tool, which the cache key does not track.',
          })}
        </p>
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {t('cache.maintenance.meta', {
            defaultValue: 'Generation {{generation}} · updated {{updatedAt}}',
            generation: config.generation,
            updatedAt: new Date(config.updatedAt).toLocaleString(),
          })}
        </p>
        <Button
          variant="ghost"
          className="gap-1.5 text-destructive"
          disabled={!canManage || pending}
          onClick={() => {
            if (!window.confirm(t('cache.maintenance.confirm', {
              defaultValue: 'Empty this bot’s cache? The next requests will call the AI service again.',
            }))) {
              return;
            }
            onPurge();
          }}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          {t('cache.maintenance.purge', { defaultValue: 'Empty the cache' })}
        </Button>
      </CardContent>
    </Card>
  );
}

function humanizeSeconds(seconds: number, t: TFunction): string {
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return t('cache.ttl.hours', { defaultValue: '{{count}} h', count: hours });
  }
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return t('cache.ttl.minutes', { defaultValue: '{{count}} min', count: minutes });
  }
  return t('cache.ttl.seconds', { defaultValue: '{{count}} s', count: seconds });
}
